import { format as dateFnsFormat } from 'date-fns';

import { axiosInstance, CATALOG_SERVICE } from '@app/adapter/axios';
import {
  Catalog,
  Product,
  ProductInput,
  Attribute,
  AttributeListResponse,
  CategoryResponse,
  LocationListResponse,
  ProductUpdate,
  ProductLocation,
  SupportedProductPublicationStatus,
  SupportedLocationType,
  ProductVariantUpdate,
} from '@app/types/catalog';
import { filterSyntaxGen } from '@app/utils';
import { getError } from '@app/utils/error';

/**
 * プロダクトのリストを取得する。
 * @param organizationId
 * @param options
 * @returns
 */
export async function getProducts(
  /** 未指定時は公開されているもの(publication.status = 'ACTIVE')のみ取得可能 */
  organizationId?: string,
  options?: {
    attributesClinicalDepartment?: string[];
    attributesJobType?: string[];
    categoryIds?: string[];
    /** 勤務日で範囲検索 */
    dateRange?: {
      end: string;
      /** 空データを抽出するか */
      isEmpty?: boolean;
      start: string;
    };
    ids?: string[];
    /** trueの場合は管理者のみ使用可能 */
    isAdmin?: boolean;
    keyword?: string;
    nextToken?: string;
    order?: 'createdAt' | string;
    organizationExpand?: boolean;
    pageNumber?: number | 0;
    pageSize?: number | 10;
    previousToken?: string;
    /** 掲載日日で範囲検索 */
    publicationRange?: {
      end: string;
      start: string;
    };
    statuses?: SupportedProductPublicationStatus[];
  }
) {
  try {
    const urlParams = [];

    if (options?.pageSize !== undefined && options?.pageNumber !== undefined) {
      urlParams.push([`$top`, options.pageSize.toString()]);
      urlParams.push([
        `$skip`,
        (options.pageNumber * options.pageSize).toString(),
      ]);
    }

    if (options?.nextToken) {
      urlParams.push(['$nextToken', options.nextToken]);
    }
    if (options?.previousToken) {
      urlParams.push(['$previousToken', options.previousToken]);
    }

    const filterParam = [];
    if (options?.keyword) {
      const keyword = options.keyword;
      const keywordFilters = [
        `category.name co '${keyword}'`,
        `name co '${keyword}'`,
        `attributes.value co '${keyword}'`,
        `customFields.startTime co '${keyword}'`,
        `customFields.endTime co '${keyword}'`,
        `description co '${keyword}'`,
        `variants.description co '${keyword}'`,
        `customFields.orderConditions co '${keyword}'`,
        `customFields.selection co '${keyword}'`,
        `additionalInformation co '${keyword}'`,
        `customFields.workPostalCode co '${keyword}'`,
        `locations.name co '${keyword}'`,
        `customFields.workAddress1 co '${keyword}'`,
        `customFields.workAddress2 co '${keyword}'`,
        `customFields.access co '${keyword}'`,
        `customFields.organizationName co '${keyword}'`,
      ];
      filterParam.push(`(${keywordFilters.join(' or ')})`);
    }

    if (options?.attributesClinicalDepartment?.length) {
      filterParam.push(
        `attributes.attributeId in ${filterSyntaxGen(
          options.attributesClinicalDepartment
        )}`
      );
    }

    if (options?.attributesJobType?.length) {
      filterParam.push(
        `attributes.attributeId in ${filterSyntaxGen(
          options.attributesJobType
        )}`
      );
    }

    if (options?.statuses?.length) {
      filterParam.push(
        `publication.status in ${filterSyntaxGen(options.statuses)}`
      );
    }

    if (options?.categoryIds?.length) {
      filterParam.push(`categoryId in ${filterSyntaxGen(options.categoryIds)}`);
    }

    if (options?.ids?.length) {
      filterParam.push(`id in ${filterSyntaxGen(options.ids)}`);
    }

    if (options?.dateRange) {
      const param = `(customFields.day ge '${dateFnsFormat(
        new Date(options.dateRange.start),
        'yyyy-MM-dd'
      )}' and customFields.day le '${dateFnsFormat(
        new Date(options.dateRange.end),
        'yyyy-MM-dd'
      )}')`;
      if (options?.dateRange.isEmpty) {
        filterParam.push(`(${param} or customFields.day eq '')`);
      } else {
        filterParam.push(param);
      }
    }

    if (options?.publicationRange) {
      filterParam.push(
        `((ymdhms(publication.until) ge '${new Date(
          options.publicationRange.start
        )
          .toISOString()
          .replace('T', ' ')
          .substring(0, 19)}' and ` +
          `ymdhms(publication.since) le '${new Date(
            options.publicationRange.end
          )
            .toISOString()
            .replace('T', ' ')
            .substring(0, 19)}')`
      );
    }

    if (filterParam.length > 0) {
      urlParams.push(['$filter', filterParam.join(' and ')]);
    }

    if (options?.order) {
      urlParams.push(['$orderBy', options?.order]);
    }

    urlParams.push([
      '$expand',
      ['variants,images,organization,category,locations'].join(' and '),
    ]);

    if (!organizationId && options?.isAdmin) {
      return await axiosInstance.get<Catalog>(
        `${CATALOG_SERVICE}/admin/products?${new URLSearchParams(
          urlParams
        ).toString()}`
      );
    }

    if (!organizationId) {
      return await axiosInstance.get<Catalog>(
        `${CATALOG_SERVICE}/products?${new URLSearchParams(
          urlParams
        ).toString()}`
      );
    }

    return await axiosInstance.get<Catalog>(
      `${CATALOG_SERVICE}/orgs/${organizationId}/products?${new URLSearchParams(
        urlParams
      ).toString()}`
    );
  } catch (error) {
    throw getError(error);
  }
}

export async function getSingleProduct(productId: string) {
  try {
    const expandParam = ['variants,images,organization,category,locations'];
    const urlParams = [];

    urlParams.push(['$expand', expandParam.join(' and ')]);

    return await axiosInstance.get<Product>(
      `${CATALOG_SERVICE}/products/${productId}?${new URLSearchParams(
        urlParams
      ).toString()}`
    );
  } catch (error) {
    throw getError(error);
  }
}

export async function createProduct(
  data: ProductInput,
  organizationId: string
) {
  try {
    return await axiosInstance.post<Product>(
      `${CATALOG_SERVICE}/orgs/${organizationId}/products`,
      data
    );
  } catch (error) {
    throw getError(error);
  }
}

export async function createProducts(
  data: ProductInput[],
  organizationId: string
) {
  try {
    return await axiosInstance.post<Product[]>(
      `${CATALOG_SERVICE}/orgs/${organizationId}/products:batchPost`,
      data
    );
  } catch (error) {
    throw getError(error);
  }
}

export async function deleteProduct(productId: string) {
  try {
    return await axiosInstance.delete<void>(
      `${CATALOG_SERVICE}/products/${productId}`
    );
  } catch (error) {
    throw getError(error);
  }
}

/**
 * Returns the promise of the batch delete request.
 *
 * @param organizationId - The id of the organization
 * @param productIds - The ids of the products
 * @returns The promise of the batch delete request
 */
export async function deleteProducts(
  organizationId: string,
  productIds: string[]
) {
  try {
    return await axiosInstance.post<void>(
      `${CATALOG_SERVICE}/orgs/${organizationId}/products:batchDelete`,
      {
        ids: productIds,
      }
    );
  } catch (error) {
    throw getError(error);
  }
}

/**
 * Returns the promise of the product copy request.
 *
 * @param productId - The id of the product
 * @returns The promise of the product copy request
 */
export async function copyProduct(productId: string) {
  try {
    return await axiosInstance.post<void>(
      `${CATALOG_SERVICE}/products/${productId}:copy`,
      null
    );
  } catch (error) {
    throw getError(error);
  }
}

/**
 * Sets the status for multiple products
 *
 * @param organizationId - The id of the organization
 * @param productIds - The ids of the products
 * @returns The promise of the set status for multiple products
 */
export async function setProductsStatus(
  organizationId: string,
  productIds: string[],
  status: SupportedProductPublicationStatus
) {
  try {
    return await axiosInstance.patch<void>(
      `${CATALOG_SERVICE}/orgs/${organizationId}/products:batchPatch`,
      [
        {
          data: {
            publication: {
              status: status.toString(),
            },
          },
          ids: productIds,
        },
      ]
    );
  } catch (error) {
    throw getError(error);
  }
}

export async function getProductsStatus(
  organizationId: string,
  productIds: string[]
) {
  try {
    const response = await axiosInstance.get<Product[]>(
      `${CATALOG_SERVICE}/orgs/${organizationId}/products:batchGet`,
      {
        params: {
          ids: productIds.join(','),
        },
      }
    );
    return response.data.map((product) => product.publication.status);
  } catch (error) {
    throw getError(error);
  }
}

export async function updateProduct(data: ProductUpdate, productId: string) {
  try {
    return await axiosInstance.patch<Product>(
      `${CATALOG_SERVICE}/products/${productId}`,
      data
    );
  } catch (error) {
    throw getError(error);
  }
}

export async function updateProducts(
  values: {
    data: ProductUpdate;
    ids: string[];
  }[],
  organizationId: string
) {
  try {
    return await axiosInstance.patch<void>(
      `${CATALOG_SERVICE}/orgs/${organizationId}/products:batchPatch`,
      values
    );
  } catch (error) {
    throw getError(error);
  }
}

export async function updateVariants(
  values: {
    data: ProductVariantUpdate;
    ids: string[];
  }[]
) {
  try {
    return await axiosInstance.patch<void>(
      `${CATALOG_SERVICE}/variants:batchPatch`,
      values
    );
  } catch (error) {
    throw getError(error);
  }
}

export function fetchCategories() {
  return axiosInstance.get<CategoryResponse>(
    `${CATALOG_SERVICE}/category-tree`
  );
}

export function getLocationById(id: string) {
  return axiosInstance.get<ProductLocation>(
    `${CATALOG_SERVICE}/locations/${id}`
  );
}

export function getLocationList(options?: {
  ids?: string[];
  level?: number;
  nextLink?: string;
  pageSize?: number;
  parentId?: string;
  type?: SupportedLocationType;
}) {
  if (options?.nextLink) {
    return axiosInstance.get<LocationListResponse>(options.nextLink);
  }
  const urlParams = [];
  const filterParam = [];

  if (options?.level !== undefined) {
    filterParam.push(`level in [${options.level}]`);
  }
  if (options?.ids?.length) {
    filterParam.push(`parentId in ${filterSyntaxGen(options.ids)}`);
  }
  if (options?.type) {
    filterParam.push(`type eq '${options?.type}'`);
  }
  if (options?.parentId) {
    filterParam.push(`parentId eq '${options?.parentId}'`);
  }

  if (filterParam.length > 0) {
    urlParams.push(['$filter', filterParam.join(' and ')]);
  }
  urlParams.push([`$top`, (options?.pageSize || 100).toString()]);

  return axiosInstance.get<LocationListResponse>(
    `${CATALOG_SERVICE}/locations?${new URLSearchParams(urlParams).toString()}`
  );
}

/**
 * 属性のリストを取得する。
 * @param options オプション
 *  - pageNumber:0始まりのページ番号
 *  - pageSize:ページごとの取得件数（1～1000）
 *  - filter:フィルタリングする属性の各プロパティ（配列で複数指定可）
 *  - nextToken:次のデータのトークン
 *  - previousToken:前のデータのトークン
 *  - order:並べ替え条件（複数指定時はカンマ区切り、降順項目には"desc"を付ける）
 * @returns
 */
export async function getAttributes(options?: {
  filter?: {
    categoryIds?: string[];
    delFlg?: number | number[];
    groupName?: string | string[];
    id?: string | string[];
    items?: {
      groupName?: string | string[];
      key?: string | string[];
      value?: string | string[];
    };
    name?: string | string[];
    order?: number | number[];
    type?: Attribute['type'] | Attribute['type'][];
  };
  nextLink?: string;
  nextToken?: string;
  order?: 'createdAt' | 'order' | string;
  pageNumber?: number;
  pageSize?: number;
  previousToken?: string;
}) {
  try {
    if (options?.nextLink) {
      return axiosInstance.get<AttributeListResponse>(options?.nextLink);
    }
    const urlParams: string[][] = [];
    if (options?.pageSize !== undefined && options?.pageNumber !== undefined) {
      urlParams.push([`$top`, options.pageSize.toString()]);
      urlParams.push([
        `$skip`,
        (options.pageNumber * options.pageSize).toString(),
      ]);
    }
    if (options?.nextToken) {
      urlParams.push(['$nextToken', options.nextToken]);
    }
    if (options?.previousToken) {
      urlParams.push(['$previousToken', options.previousToken]);
    }
    if (options?.filter) {
      const param = getFilterParam({ delFlg: 0, ...options.filter }); // NOTE:デフォルトでは未削除のものだけ抽出する
      if (param) {
        urlParams.push(['$filter', param]);
      }
    }
    urlParams.push(['$orderBy', options?.order?.toString() ?? 'order']);
    urlParams.push(['$expand', 'category,organization']);

    return await axiosInstance.get<AttributeListResponse>(
      `${CATALOG_SERVICE}/attributes?${new URLSearchParams(
        urlParams
      ).toString()}`
    );
  } catch (error) {
    throw getError(error);
  }
}

/**
 * フィルターパラメータ（$filter）用の値を取得する。
 * 下記オブジェクトを渡すと
 * {param1:"a", param2:123, param3:["a","b","c"], param4:{param4_1:"a", param4_2:123}}
 * 下記文字列を返す。
 * "param1 eq 'a' and param2 eq 123 and param3 in ['a','b','c'] and param4.param4_1 eq 'a' and param4.param4_2 eq 123"
 * オブジェクトの配列には非対応。（それでフィルターすることはないと思われる）
 * @param filter フィルターに使うオブジェクト
 * @param paramName ※再起処理用なので使わない
 * @returns
 */
function getFilterParam<T extends object>(
  filter: T,
  paramName?: string
): string {
  const entries = Object.entries(filter);
  const filterParam = entries
    .map((item) => {
      const key = paramName ? `${paramName}.${item[0]}` : item[0];
      const value = item[1];
      if (!key || value === undefined) {
        return '';
      }
      if (Array.isArray(value)) {
        if (!value.length) {
          return '';
        }
        if (typeof value[0] === 'string') {
          // NOTE:シングルコーテーションのエスケープが必要かも
          return `${key} in [${value.map((v) => `'${v}'`).join(',')}]`;
        }
        return `${key} in [${value.join(',')}]`;
      }
      if (typeof value === 'object') {
        return getFilterParam(value, key);
      }
      if (typeof value === 'string') {
        return `${key} eq '${value.toString()}'`;
      }
      return `${key} eq ${value.toString()}`;
    })
    .filter((v) => v);
  if (!filterParam.length) {
    return '';
  }
  return filterParam.join(' and ');
}

export function getLocationByLevel() {
  const filterParam = `level eq 1`;
  const urlParams = new URLSearchParams();
  urlParams.append('$filter', filterParam);
  urlParams.append('$top', '50');

  return axiosInstance.get<LocationListResponse>(
    `${CATALOG_SERVICE}/locations?${urlParams}`
  );
}

export function getLocationByParentId(parentId: string) {
  const urlParams = new URLSearchParams();
  urlParams.append('$filter', `parentId eq '${parentId}' and level eq 2`);
  urlParams.append('$top', '100');

  return axiosInstance.get<LocationListResponse>(
    `${CATALOG_SERVICE}/locations?${urlParams}`
  );
}
